'use client';

/**
 * ActivitiesSection Component
 *
 * A section that displays activity cards in a horizontally scrollable container with a "See more" button.
 * Gradient overlays indicate when there's more content to scroll to.
 */
import React from 'react';

import { HorizontalSlider } from '../../components/common/horizontalSlider';
import { Section } from '../../components/common/section/Section';
import { ActivityCard } from '../../components/features/activity/activityCard';
import type { ActivityAvailability } from '../../components/features/activity/unavailableBadge';
import { ActivityCardSkeleton } from '../../components/features/skeletons/ActivityCardSkeleton';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

export interface ActivitiesSectionProps {
  /**
   * Array of activity objects to display
   */
  activities: {
    id: string;
    imageUrl: string;
    title: string;
    professionalName: string;
    isAtHome?: boolean;
    /** Real delivery-mode mix; 2+ kinds render as compact chips on the card. */
    locationKinds?: ('venue' | 'at_home' | 'online')[];
    /** Short venue label (typically the city); shown instead of `atVenueText`. */
    city?: string;
    bookingsSinceLastYear?: number;
    /** `null`/`undefined` → no priced booking yet; rendered as `contactForPriceText`. */
    startingPrice?: number | null;
    /** 'none' = approved but not bookable yet (badged on the card). */
    availability?: ActivityAvailability;
  }[];

  /**
   * Handler for when an activity card is clicked
   */
  onActivityClick: (activityId: string) => void;

  /**
   * Builds the activity page URL for a card. When provided, each card exposes a
   * real crawlable link; plain clicks still go through `onActivityClick`.
   */
  getActivityHref?: (activityId: string) => string;

  /**
   * Handler for when the "See more" button is clicked
   */
  onSeeMoreClick: () => void;

  /**
   * Whether the activities are currently loading
   */
  isLoading?: boolean;

  /**
   * Whether there was an error loading the activities
   */
  isError?: boolean;

  /**
   * Translated heading text for the section
   * @default "Activities"
   */
  headingText?: string;

  /**
   * Translated text for the "See more" button
   * @default "See more"
   */
  seeMoreText?: string;

  /**
   * Translated text for the "No activities found" message
   * @default "No activities found."
   */
  noActivitiesText?: string;

  /**
   * Translated text for "At Home" location in ActivityCard
   * @default "At Home"
   */
  atHomeText?: string;

  /**
   * Translated text for "At Venue" location in ActivityCard
   * @default "At Venue"
   */
  atVenueText?: string;

  /**
   * Translated text for an online (visio) delivery in ActivityCard. Drives the
   * online chip when an activity offers a mixed venue/at-home/online set.
   * @default "Online"
   */
  onlineText?: string;

  /**
   * Translated prefix for bookings count text in ActivityCard
   * @default ""
   */
  bookingsInYearPrefix?: string;

  /**
   * Translated middle text between count and year for bookings text in ActivityCard
   * @default " Bookings since "
   */
  bookingsInYearMiddle?: string;

  /**
   * Translated suffix for bookings count text in ActivityCard
   * @default ""
   */
  bookingsInYearSuffix?: string;

  /**
   * Translated text for price suffix ("onwards") in ActivityCard
   * @default "onwards"
   */
  onwardsText?: string;

  /**
   * Translated label for free bookings in ActivityCard
   * @default "Free"
   */
  freeText?: string;

  /**
   * Badge text for activities that are not bookable yet (availability 'none')
   * @default "No availability yet"
   */
  unavailableText?: string;

  /**
   * Whether the section should extend to the full width of the viewport
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Whether to disable the default vertical padding
   * @default false
   */
  disableVerticalPadding?: boolean;

  /**
   * Whether to show the "See more" button
   * @default true
   */
  showSeeMore?: boolean;

  /**
   * Number of skeleton cards to show when loading
   * @default 4
   */
  skeletonCount?: number;
}

/**
 * ActivitiesSection component for displaying a horizontally scrollable list of activity cards
 *
 * @example
 * ```tsx
 * <ActivitiesSection
 *   activities={activities}
 *   onActivityClick={(id) => console.log(`Activity ${id} clicked`)}
 *   onSeeMoreClick={() => console.log('See more clicked')}
 * />
 * ```
 */
export const ActivitiesSection: React.FC<ActivitiesSectionProps> = ({
  activities,
  onActivityClick,
  getActivityHref,
  onSeeMoreClick,
  isLoading = false,
  isError = false,
  headingText = 'Activities',
  seeMoreText = 'See more',
  noActivitiesText = 'No activities found.',
  atHomeText = 'At Home',
  atVenueText = 'At Venue',
  onlineText = 'Online',
  bookingsInYearPrefix = '',
  bookingsInYearMiddle = ' Bookings since ',
  bookingsInYearSuffix = '',
  onwardsText = 'onwards',
  freeText = 'Free',
  unavailableText = 'No availability yet',
  fullWidth = false,
  disableVerticalPadding = false,
  showSeeMore = true,
  skeletonCount = 4
}) => {
  return (
    <Section
      disableVerticalPadding={true}
      className={cn('relative', !disableVerticalPadding && 'py-6 md:py-15')}
      fullWidth={fullWidth}
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md md:text-xl text-neutral-800 mb-3 font-semibold md:font-medium">{headingText}</h3>

        {showSeeMore && (
          <Button variant="link" onClick={onSeeMoreClick} className="font-medium">
            {seeMoreText}
          </Button>
        )}
      </div>

      {/* Error state */}
      {isError && <div className="py-4 text-destructive">Failed to load activities. Please try again later.</div>}

      {/* Horizontal slider with smooth drag and gradient overlays */}
      <HorizontalSlider gap="gap-4" minHeight="212px">
        {isLoading ? (
          Array.from({ length: skeletonCount }, (_, i) => `activity-skeleton-${i}`).map((key) => (
            <ActivityCardSkeleton key={key} variant="card" className="flex-shrink-0 w-48" />
          ))
        ) : activities.length > 0 ? (
          activities.map((activity) => (
            <ActivityCard
              key={activity.id}
              imageUrl={activity.imageUrl}
              title={activity.title}
              professionalName={activity.professionalName}
              isAtHome={activity.isAtHome}
              locationKinds={activity.locationKinds}
              isOnline={(activity.locationKinds?.length ?? 0) === 1 && activity.locationKinds?.[0] === 'online'}
              city={activity.city}
              bookingsSinceLastYear={activity.bookingsSinceLastYear}
              startingPrice={activity.startingPrice}
              unavailableText={activity.availability === 'none' ? unavailableText : undefined}
              onClick={() => onActivityClick(activity.id)}
              href={getActivityHref?.(activity.id)}
              className="flex-shrink-0 w-48"
              atHomeText={atHomeText}
              atVenueText={atVenueText}
              onlineText={onlineText}
              bookingsInYearPrefix={bookingsInYearPrefix}
              bookingsInYearMiddle={bookingsInYearMiddle}
              bookingsInYearSuffix={bookingsInYearSuffix}
              onwardsText={onwardsText}
              freeText={freeText}
            />
          ))
        ) : !isError ? (
          <div className="flex items-center justify-center w-full text-muted-foreground">{noActivitiesText}</div>
        ) : null}
      </HorizontalSlider>
    </Section>
  );
};
